import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { Conversation } from '@tips-crm/shared';
import {
  fetchContacts,
  fetchConversations,
  fetchReportSummary,
} from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { StatCard } from '../components/StatCard.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Loading, ErrorState } from '../components/States.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function ResellerPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sellerId = user?.seller_id ?? undefined;

  // Server enforces seller scope from the JWT; seller_id here is advisory only.
  const summaryQ = useQuery({
    queryKey: ['reseller-summary', sellerId],
    queryFn: () => fetchReportSummary({ seller_id: sellerId }),
  });

  const contactsQ = useQuery({
    queryKey: ['reseller-contacts', sellerId],
    queryFn: () => fetchContacts({ seller_id: sellerId, limit: 10 }),
  });

  const convQ = useQuery({
    queryKey: ['reseller-conversations', sellerId],
    queryFn: () => fetchConversations({ seller_id: sellerId, limit: 10 }),
  });

  const convColumns = useMemo<Column<Conversation>[]>(
    () => [
      {
        key: 'channel',
        header: 'Channel',
        render: (c) => <span className={`badge badge--${c.channel}`}>{c.channel}</span>,
      },
      { key: 'direction', header: 'Direction', render: (c) => c.direction },
      {
        key: 'status',
        header: 'Status',
        render: (c) => <span className={`badge badge--status-${c.status}`}>{c.status}</span>,
      },
      { key: 'disposition', header: 'Disposition', render: (c) => c.disposition ?? '—' },
      { key: 'started', header: 'Started', render: (c) => formatDate(c.started_at) },
    ],
    [],
  );

  return (
    <div className="page">
      <PageHeader
        title="Reseller Portal"
        subtitle={user ? `Welcome, ${user.display_name}` : undefined}
      />

      <section>
        {summaryQ.isLoading && <Loading />}
        {summaryQ.isError && <ErrorState error={summaryQ.error} onRetry={() => summaryQ.refetch()} />}
        {summaryQ.data && (
          <div className="stat-grid">
            <StatCard
              label="Total conversations"
              value={summaryQ.data.total_conversations}
            />
            <StatCard
              label="Inbound calls"
              value={summaryQ.data.inbound_calls}
            />
            <StatCard label="Inbound chats" value={summaryQ.data.inbound_chats} />
            <StatCard
              label="Summary coverage"
              value={`${Math.round(summaryQ.data.summary_coverage_pct)}%`}
            />
          </div>
        )}
      </section>

      <section className="card">
        <div className="card__title-row">
          <h2 className="card__title">Recent conversations</h2>
          <Link to="/conversations" className="btn btn--ghost btn--sm">
            View all
          </Link>
        </div>
        <DataTable
          columns={convColumns}
          rows={convQ.data?.items}
          rowKey={(c) => c.id}
          isLoading={convQ.isLoading}
          isError={convQ.isError}
          error={convQ.error}
          onRetry={() => convQ.refetch()}
          onRowClick={(c) => navigate(`/conversations/${c.id}`)}
          emptyMessage="No conversations yet."
        />
      </section>

      <section className="card">
        <div className="card__title-row">
          <h2 className="card__title">Recent contacts</h2>
          <Link to="/contacts" className="btn btn--ghost btn--sm">
            View all
          </Link>
        </div>
        <DataTable
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (c) =>
                [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || '—',
            },
            { key: 'email', header: 'Email', render: (c) => c.email ?? '—' },
            { key: 'phone', header: 'Phone', render: (c) => c.phone_e164 ?? '—' },
          ]}
          rows={contactsQ.data?.items}
          rowKey={(c) => c.id}
          isLoading={contactsQ.isLoading}
          isError={contactsQ.isError}
          error={contactsQ.error}
          onRetry={() => contactsQ.refetch()}
          onRowClick={(c) => navigate(`/contacts/${c.id}`)}
          emptyMessage="No contacts yet."
        />
      </section>
    </div>
  );
}
